import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { NotificationTypeEnum } from '../enums/NotificationTypeEnum';
import { FileService } from '../services/fileService';

import { useAuth } from './useAuth';
import { useNotifications } from './useNotifications';
import { GetFileResponse } from '../interfaces/FileService';

const fileService = new FileService();

function useProtectedFile() {
  const { t } = useTranslation();
  const { createNotification } = useNotifications();
  const navigate = useNavigate();
  const { fileUid } = useParams<{ fileUid: string }>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDownloadHelpTextVisible, setIsDownloadHelpTextVisible] =
    useState<boolean>(false);

  const { isLoggedIn, authenticatedUser, redirectLoggedInUser } = useAuth();

  const [file, setFile] = useState<GetFileResponse | null>();
  const [otp, setOtp] = useState<number | null>(null);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [expirationTime, setExpirationTime] = useState<number | null>(null);
  const memoizedRedirectLoggedInUser = useCallback(redirectLoggedInUser, []);
  const exeFileUrl = `${import.meta.env.VITE_APP_AZURE_STORAGE_URL}/${import.meta.env.VITE_APP_AZURE_RESOURCES_CONTAINER}/Filegrant Viewer.exe?v=${Date.now()}`;

  const OTP_DURATION = 30000;
  /**
   * Retrieves information about a specified file.
   *
   * This asynchronous function utilizes the `fileService` to fetch file information based on a specified `fileId`.
   * It manages loading state, handles potential errors, and provides notifications for the user.
   *
   * @function
   * @name getFileOtp
   *
   * @property {string} fileId - The unique identifier of the file whose information is being retrieved.
   *
   * @returns {Promise<void>} A promise that resolves when the file information retrieval process is complete.
   */
  const getFileOtp = useCallback(async () => {
    if (fileUid) {
      setIsLoading(true);
      fileService
        .getFileOtp(parseInt(fileUid))
        .then((response) => {
          setOtp(response);
          const expire = Date.now() + OTP_DURATION;
          setExpirationTime(expire);
          setTimeLeft(OTP_DURATION);
        })
        .catch(() => {
          createNotification(
            t('errors.requestFilePermissions'),
            NotificationTypeEnum.DANGER
          );
          setOtp(null);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      if (isLoggedIn) {
        memoizedRedirectLoggedInUser();
      }
    }
  }, [
    fileUid,
    navigate,
    isLoggedIn,
    createNotification,
    memoizedRedirectLoggedInUser,
    t,
    authenticatedUser?.userId,
  ]);

  /**
   * Function to request and return file data based on the provided file ID.
   *
   * This function sets the loading state to true and shows a loading overlay
   * while the file request is being processed. It then attempts to retrieve the
   * file using the file service. If the request succeeds, the response is returned.
   * If the request fails, an error notification is created and the selected file
   * is set to null. Finally, the loading state is set to false.
   *
   * @returns {Promise<*>} A promise that resolves to the response of the file request.
   * @param fileUid
   */
  const getFile = useCallback(async () => {
    if (!fileUid) return;
    setIsLoading(true);
    return fileService
      .getFile(fileUid?.toString())
      .then((response) => {
        setFile(response);
      })
      .catch(() => {
        createNotification(
          t('errors.requestFilePermissions'),
          NotificationTypeEnum.DANGER
        );
        setFile(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [fileUid, createNotification, t]);

  /**
   * Determines if the user's operating system is a Windows OS.
   *
   * This function checks the user agent string of the browser to identify
   * whether the current operating system is Windows-based. It performs a
   * case-insensitive match for the substring 'windows' within the user agent.
   *
   * @returns {boolean} True if the user's OS is Windows, false otherwise.
   */
  const isWindowsOS = (): boolean => {
    const userAgent = navigator.userAgent.toLowerCase();
    return userAgent.includes('windows');
  };

  /**
   * A memoized boolean that indicates whether the operating system is Windows.
   *
   * This variable uses `useMemo` to ensure that the determination of the OS
   * being Windows is only computed once based on the environment during the
   * initial rendering. The memoization is done to optimize performance by
   * avoiding repetitive checks on re-renders.
   *
   * The determination is based on the `isWindowsOS` function which encapsulates
   * the logic for identifying a Windows OS.
   *
   * @type {boolean}
   */
  const isWindowsPlatform: boolean = useMemo(() => {
    return isWindowsOS();
  }, []);

  /**
   * Initiates the download of a file named 'FilegrantViewer.exe' when invoked. If the OTP (One Time Password) is available,
   * it creates a temporary anchor element to simulate a download click and manages its visibility.
   *
   * The method performs the download by programmatically creating a hyperlink element,
   * setting its `href` attribute to a predefined executable file URL `exeFileUrl`,
   * and its `download` attribute to the desired filename. The link is appended to the document body,
   * triggered for a click event to start the download, and subsequently removed from the document.
   *
   * Additionally, this method sets the visibility of download help text to true using the function
   * `setIsDownloadHelpTextVisible`, indicating that the download process has been initiated.
   */
  const handleDownloadFile = () => {
    if (otp) {
      // Create a fake download link and click it to download the file
      const link = document.createElement('a');
      link.href = exeFileUrl;
      link.download = 'Filegrant Viewer.exe';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setIsDownloadHelpTextVisible(true);
    }
  };

  const formatTime = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const mm = String(minutes).padStart(2, '0');
    const ss = String(seconds).padStart(2, '0');
    return `${mm}:${ss}`;
  };

  useEffect(() => {
    if (expirationTime !== null) {
      const interval = setInterval(() => {
        const now = Date.now();
        const diff = expirationTime - now;
        if (diff <= 0) {
          setExpirationTime(null);
          setTimeLeft(0);
          clearInterval(interval);
        } else {
          setTimeLeft(diff);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [expirationTime]);

  useEffect(() => {
    Promise.all([getFileOtp(), getFile()]).then(() => {});
  }, [getFileOtp]);

  return {
    isLoading,
    otp,
    file,
    t,
    handleDownloadFile,
    isWindowsPlatform,
    isDownloadHelpTextVisible,
    formatTime,
    timeLeft,
    getFileOtp,
  };
}

export default useProtectedFile;
